import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { ProfileActions } from "../redux/profileAction";
import { updateProfileAsync } from "../redux/profileApi";
import axios from "axios";
import { showSuccessSnackbar } from "../../snackBar/snackBar.action";
import TwoFactorModal from "./components/TwoFactorModal";

const ProfileBasicDetails = (props) => {

  const dispatch = useDispatch();

  const { profileEmailChangeModal, profileContactChangeModal } = useSelector(
    (state) => state.profile,
    shallowEqual
  );
  useEffect(() => {
    if (props && props.selectedTab !== "basic-details") {
      formik.resetForm();//reset form
    }
  }, [props && props.selectedTab]);

  const { user, isLoading, authToken } = useSelector(
    (state) => state.auth,
    shallowEqual
  );

  const role = user?.role;
  const initialValues = {
    email: user.email || "",
    displayName: user.displayName || "",
    firstName: user.firstName || "",
    lastName: user.lastName || ""
  };
  //formik validation schema
  const ProfileSchema = () =>
    Yup.object().shape({
      // firstName: Yup.string()
      //   .trim()
      //   .required("First name is required"),
      // lastName: Yup.string()
      //   .trim()
      //   .required("Last name is required"),
      email: Yup.string()
        .trim()
        .email("Enter valid email")
        .min(3, "Minimum 3 characters")
        .max(50, "Maximum 50 characters")
        .required("Email is required")
    });

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: ProfileSchema,
    onSubmit: (values, { setErrors, resetForm }) => {

      let updatedValues = {}
      // updatedValues.displayName = `${values.firstName} ${values.lastName}`
      // updatedValues.firstName = values.firstName
      // updatedValues.lastName = values.lastName
      updatedValues.displayName = values.displayName

      if (values.email !== user.email) {
        updatedValues.email = values.email
      }

      dispatch(updateProfileAsync(updatedValues, user._id));//call update profile details api
      //resetForm();
    },
  });

  const [twoFALoading, setTwoFALoading] = useState(false);
  const [twoFASetupModal, setTwoFASetupModal] = useState(false);
  const [twoFASetupPayload, setTwoFASetupPayload] = useState(null);
  const [otpError, setOtpError] = useState("");
  const [otpVerifying, setOtpVerifying] = useState(false);
  const deriveTwoFAEnabled = (twoFAData) =>
    Boolean(
      twoFAData?.isEnabled ??
        twoFAData?.twoFactorAuthentication ??
        user?.twoFactorAuthentication ??
        false
    );

  const [twoFAProfile, setTwoFAProfile] = useState(
    user?.twoFactorAuth || null
  );
  const [twoFAEnabled, setTwoFAEnabled] = useState(
    deriveTwoFAEnabled(user?.twoFactorAuth)
  );

  const authHeaders = {
    "Content-Type": "application/json",
    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
  };

  const resetTwoFAState = () => {
    setTwoFASetupModal(false);
    setTwoFASetupPayload(null);
    setOtpError("");
  };

  useEffect(() => {
    const fetchTwoFAProfile = async () => {
      if (!user?._id) return;
      try {
        const { data } = await axios({
          method: "GET",
          url: `${process.env.REACT_APP_HOST}${process.env.REACT_APP_ADMIN_BASE_URL}/getAdminProfile/${user?._id}`,
          headers: authHeaders,
        });
        if (data) {
          const twoFAData =
            data?.data?.user?.twoFactorAuth ??
            data?.data?.twoFactorAuth ??
            data?.twoFactorAuth ??
            null;
          setTwoFAProfile(twoFAData);
          setTwoFAEnabled(deriveTwoFAEnabled(twoFAData));
        }
      } catch (error) {
        // silently fail; UI will rely on existing state
      }
    };

    fetchTwoFAProfile();
  }, [user?._id]);

  useEffect(() => {
    setTwoFAEnabled(deriveTwoFAEnabled(user?.twoFactorAuth));
  }, [user?.twoFactorAuthentication, user?.twoFactorAuth]);

  const handleTwoFASetup = async () => {
    if (twoFALoading) return;
    try {
      setTwoFALoading(true);
      const { data } = await axios({
        method: "POST",
        url: `${process.env.REACT_APP_HOST}${process.env.REACT_APP_ADMIN_BASE_URL}/twoFactor/setup`,
        headers: authHeaders,
        data: { user_id: user?._id },
      });
      if (data?.statusCode === 200) {
        setTwoFASetupPayload(data?.data || {});
        setTwoFASetupModal(true);
      } else {
        dispatch(
          showSuccessSnackbar(
            "error",
            data?.message || "Unable to enable Two Factor Authentication",
            3000
          )
        );
      }
    } catch (error) {
      dispatch(
        showSuccessSnackbar(
          "error",
          error?.response?.data?.message ||
            "Unable to enable Two Factor Authentication",
          3000
        )
      );
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleTwoFADisable = async () => {
    if (twoFALoading || !twoFAEnabled) return;
    try {
      setTwoFALoading(true);
      const { data } = await axios({
        method: "POST",
        url: `${process.env.REACT_APP_HOST}${process.env.REACT_APP_ADMIN_BASE_URL}/twoFactor/disable`,
        headers: authHeaders,
        data: { user_id: user?._id },
      });
      if (data?.statusCode === 200) {
        dispatch(
          ProfileActions.profileUpdateSuccess({
            ...user,
            twoFactorAuthentication: false,
          })
        );
        setTwoFAProfile((prev) => ({ ...(prev || {}), isEnabled: false }));
        setTwoFAEnabled(false);
        dispatch(
          showSuccessSnackbar(
            "success",
            "2FA Disabled Successfully",
            3000
          )
        );
      } else {
        dispatch(
          showSuccessSnackbar(
            "error",
            data?.message || "Unable to disable Two Factor Authentication",
            3000
          )
        );
      }
    } catch (error) {
      dispatch(
        showSuccessSnackbar(
          "error",
          error?.response?.data?.message ||
            "Unable to disable Two Factor Authentication",
          3000
        )
      );
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleOtpVerify = async (otpCode) => {
    if (!otpCode || otpCode.length !== 6) {
      setOtpError("Enter the 6 digit code from Google Authenticator.");
      return;
    }
    try {
      setOtpVerifying(true);
      const { data } = await axios({
        method: "POST",
        url: `${process.env.REACT_APP_HOST}${process.env.REACT_APP_ADMIN_BASE_URL}/twoFactor/verify`,
        headers: authHeaders,
        data: { otp: otpCode },
      });
      if (data?.statusCode === 200) {
        dispatch(
          ProfileActions.profileUpdateSuccess({
            ...user,
            twoFactorAuthentication: true,
          })
        );
        setTwoFAProfile((prev) => ({ ...(prev || {}), isEnabled: true }));
        setTwoFAEnabled(true);
        dispatch(
          showSuccessSnackbar("success", "2FA Enabled Successfully", 3000)
        );
        resetTwoFAState();
      } else {
        setOtpError(data?.message || "Invalid OTP. Please try again.");
        dispatch(
          showSuccessSnackbar(
            "error",
            data?.message || "Unable to verify OTP. Please try again.",
            3000
          )
        );
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Unable to verify OTP. Please try again.";
      setOtpError(message);
      dispatch(showSuccessSnackbar("error", message, 3000));
    } finally {
      setOtpVerifying(false);
    }
  };

  return (
    <>
      <div className="tblmrgn mt-0 lwpddng cstm_pddng">
        <div className="row justify-content-center">
          <div className="col-xl-5 col-md-6-as">
            <form
              className="form def_form frmwtpddng"
              onSubmit={formik.handleSubmit}
            >
              <div className="row">
                {/* <div className="col-md-12">
                  <div className="form-group">
                    <label className="form-label">First Name</label>
                    <input
                      placeholder="First Name"
                      type="text"
                      className={`form-control wth_chng border-less-input`}
                      name="firstName"
                      {...formik.getFieldProps("firstName")}
                    />
                    {formik.touched.firstName && formik.errors.firstName ? (
                      <div className="fv-plugins-message-container">
                        <div className="fv-help-block">
                          {formik.errors.firstName}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="form-group">
                    <label className="form-label">Last Name</label>
                    <input
                      placeholder="Last Name"
                      type="text"
                      className={`form-control wth_chng border-less-input`}
                      name="lastName"
                      {...formik.getFieldProps("lastName")}
                    />
                    {formik.touched.lastName && formik.errors.lastName ? (
                      <div className="fv-plugins-message-container">
                        <div className="fv-help-block">
                          {formik.errors.lastName}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div> */}
                <div className="col-md-12">
                  <div className="form-group">
                    <label className="form-label">Display Name</label>
                    <input
                      placeholder="Display Name"
                      type="text"
                      className={`form-control wth_chng border-less-input`}
                      name="displayName"
                      {...formik.getFieldProps("displayName")}
                    />
                    {formik.touched.displayName && formik.errors.displayName ? (
                      <div className="fv-plugins-message-container">
                        <div className="fv-help-block">
                          {formik.errors.displayName}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="col-lg-12">
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <div className="re_grpPhone position-relative">
                      <input
                        placeholder="username@company.com"
                        type="email"
                        className={`form-control wth_chng border-less-input`}
                        name="email"
                        {...formik.getFieldProps("email")}
                      />
                    </div>
                    {formik.touched.email && formik.errors.email ? (
                      <div className="fv-plugins-message-container">
                        <div className="fv-help-block">
                          {formik.errors.email}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
                {role === 2 && (
                  <div className="col-lg-12">
                    <div className="form-group mb-0">
                      <label className="form-label d-flex justify-content-between align-items-center">
                        <span>2FA Google Authenticator</span>
                        <span
                          className={`badge badge-${
                            twoFAEnabled ? "success" : "secondary"
                          }`}
                        >
                          {twoFAEnabled ? "Enabled 2FA" : "Disabled 2FA"}
                        </span>
                      </label>
                      <p className="text-muted mb-3">
                        Protect your admin account with an additional verification
                        step in Google Authenticator.
                      </p>
                      <div className="d-flex flex-column align-items-start gap-2">
                        <button
                          type="button"
                          className="btn btn-blue defpddng show-more-btn font-family-base btn_new"
                          style={{ minWidth: "150px", padding: "0.6rem 1.5rem" }}
                          disabled={twoFALoading}
                          onClick={
                            twoFAEnabled
                              ? handleTwoFADisable
                              : handleTwoFASetup
                          }
                        >
                          {twoFALoading
                            ? "Please wait..."
                            : twoFAEnabled
                            ? "Disable 2FA"
                            : "Enable 2FA"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="form-group d-flex flex-wrap justify-content-center align-items-center mb-0 mt-4">
                <button
                  type="submit"
                  disabled={Object.keys(formik.errors).length > 0 || isLoading}
                  className="btn btn-blue defpddng show-more-btn font-family-base mt-3 btn_new"
                >
                  <span>Update</span>
                  {isLoading && (
                    <span className="ml-3 spinner spinner-white"></span>
                  )}
                </button>
              </div>
            </form>
            {/*   <ProfileEmailVerifyModal
              profileEmailChangeModal={profileEmailChangeModal}
              onCloseEmailChangeModal={onCloseEmailChangeModal}
              emailId={formik.values.emailId}
            /> */}
          </div>
        </div>
      </div>
      <TwoFactorModal
        open={twoFASetupModal}
        onClose={resetTwoFAState}
        qrCode={twoFASetupPayload?.qrCode}
        manualEntryKey={twoFASetupPayload?.manualEntryKey}
        otpauthUrl={twoFASetupPayload?.otpauthUrl}
        onVerifyOtp={handleOtpVerify}
        loading={otpVerifying}
        error={otpError}
      />
    </>
  );
};

export default ProfileBasicDetails;
